import { useLazyQuery, useQuery } from '@apollo/client';
import {
  DropdownIndicator,
  formatGroupLabel,
  IndicatorSeparator
} from '@nile/components/src/Pebbles/select';
import { AccountClient, GET_CLIENTS } from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import {
  useSiteListForDropdown,
  useProduct,
  AccessControl
} from '@nile/components';

const ClientSideDropDown = (props) => {
  const { selectedSite, selectedClient, onChange, flexColumn, theme } = props;
  const [sites, setSites] = useState([]);
  const [clients, setClients] = useState([]);
  const isMSPProduct = useProduct('MSP');

  useEffect(() => {
    if (!selectedClient) setSites([]);
  }, [selectedClient]);

  useQuery(GET_CLIENTS, {
    client: AccountClient,
    skip: !isMSPProduct, // skip for IT product
    variables: {
      listInfo: {
        sort: {
          attribute: 'name',
          order: 'ASC'
        }
      }
    },
    onCompleted: (res) => {
      const processedClients = res?.getClientList?.clients.map((client) => {
        return {
          label: client.name,
          value: client.name,
          ...client
        };
      });
      setClients(processedClients || []);
    }
  });
  const { getClientSites, data } = useSiteListForDropdown();

  useEffect(() => {
    if (data) {
      const processedSites = data.map((site) => {
        return {
          label: site.name,
          value: site.name,
          ...site
        };
      });
      setSites(processedSites || []);
    }
  }, [data]);

  /**
   *
   * @param {object} val
   * Performing API call to fetch site based on client selection
   */
  useEffect(() => {
    if (!isMSPProduct) {
      // if IT product, get all sites list
      getClientSites();
    }
  }, []);
  const getSiteData = (val) => {
    getClientSites(val.accountId);
  };

  /**
   *
   * @param {object} val
   * @param {string} type
   *
   * Updating filter condition object with selections from clien/site dropdown and filter sheet
   */
  const handleClientSiteChange = (val, type) => {
    if (type === 'client.name' && val) {
      getSiteData(val);
    }
    onChange(val, type);
  };

  const indicatorComponent = !theme && DropdownIndicator;
  return (
    <StyledToolbar>
      <div
        className={`${
          flexColumn ? 'flex-column' : 'flex-row client-site-select-container'
        }`}
      >
        <AccessControl
          productToCheck="MSP"
          render={
            <div
              className={`flex-align-center ${
                flexColumn ? 'custom-padding' : 'flex-row item-select-container'
              }`}
            >
              {flexColumn ? (
                <p>
                  <FormattedMessage id="common.client" />
                </p>
              ) : (
                <div className="mr10 pl10 fx center">
                  <SVGIcon name="ClientLogo" size="md" />
                </div>
              )}

              <Select
                className={`${
                  flexColumn ? 'select-full-width' : 'select-block'
                }`}
                isClearable
                onChange={(val) => {
                  handleClientSiteChange(val, 'client.name');
                }}
                components={{
                  IndicatorSeparator,
                  indicatorComponent
                }}
                menuPortalTarget={document.body}
                classNamePrefix="value_selector"
                placeholder="Choose Client"
                formatGroupLabel={formatGroupLabel}
                menuPlacement="auto"
                value={selectedClient}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 })
                }}
                options={clients}
              />
            </div>
          }
        />
        <div
          className={`flex-align-center ${
            flexColumn ? 'custom-padding' : 'flex-row item-select-container'
          }`}
        >
          {flexColumn ? (
            <p>
              <FormattedMessage id="attribute.siteId" />
            </p>
          ) : (
            <div className="mr10 pl10 fx center">
              <SVGIcon name="Site" size="md" />
            </div>
          )}

          <Select
            className={`${flexColumn ? 'select-full-width' : 'select-block'}`}
            isClearable
            onChange={(val) => {
              handleClientSiteChange(val, 'site.name');
            }}
            components={{
              IndicatorSeparator,
              indicatorComponent
            }}
            menuPortalTarget={document.body}
            classNamePrefix="value_selector"
            placeholder="Choose Site"
            formatGroupLabel={formatGroupLabel}
            menuPlacement="auto"
            value={selectedSite}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 })
            }}
            options={sites}
          />
        </div>
      </div>
    </StyledToolbar>
  );
};

const StyledToolbar = styled.div`
  .select-block {
    width: 180px;
  }
  .select-full-width {
    width: 100%;
  }
  .custom-padding {
    padding-left: 15px;
    padding-top: 10px;
    // padding-bottom: 15px;
    background: #151516;
    padding-right: 15px;
    margin-left: 20px;
    border-radius: 5px;
    margin-top: 10px;
    p {
      color: rgba(255, 255, 255, 0.8);
    }
    .value_selector__control {
      background-color: #151516;
      border-bottom: 1px solid #151516 !important;
      &:hover {
        border-color: #f30;
      }
      .value_selector__single-value {
        color: rgba(255, 255, 255, 0.5);
      }
    }

    .value-select__menu {
      background-color: #000;
      border: 1px solid #000;
    }

    .value-select__option {
      background-color: #000;

      &:hover {
        background-color: #000;
      }
    }

    .value-select__indicator-separator {
      background-color: var(--border-color);
    }

    .value-select__placeholder,
    .value-select__single-value {
      color: var(--text-primary);
    }
  }

  .item-select-container {
    border: 0.5px solid #eeeeee;
  }
  .client-site-select-container {
    background: #ffffff;
    mix-blend-mode: normal;
    border: 1px solid #eeeeee;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.102874);
    border-radius: 4px;
    margin-right: 10px;
    margin-bottom: 3px;
  }
`;

export default ClientSideDropDown;
